import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import { SubscriptionStatus } from "./ApiTransport";
import { Subscription } from "./ApiTypes";
import { showDate } from "./Date";
import { DataEntitiesSubscriptionStatus } from "../shared/api/axios-client";

export enum SubscriptionErrorType {
    PlanIsNotListed = 'PlanIsNotListed',
    FacilityIdMismatch = 'FacilityIdMismatch',
    SubscriptionNotFound = 'SubscriptionNotFound',
    PlanEntryIsNotLatest = 'PlanEntryIsNotLatest',
    NoPaymentMethod = 'NoPaymentMethod',
    SubscriptionEntryNotFound = 'SubscriptionEntryNotFound',
    PlanEntryNotFound = 'PlanEntryNotFound',
    PlanPriceNotFound = 'PlanPriceNotFound',
    PaymentFailed = 'PaymentFailed',
    OrderNotFound = 'OrderNotFound',
    CouldNotRecordPayment = 'CouldNotRecordPayment',
    CannotSubscribeToOldPlanEntry = 'CannotSubscribeToOldPlanEntry',
    CouldNotRecordOrder = 'CouldNotRecordOrder',
    SubscriptionNotActive = 'SubscriptionNotActive',
    NoQuantityChange = 'NoQuantityChange',
    QuantityDecrease = 'QuantityDecrease',
    InvalidQuantityChange = 'InvalidQuantityChange',
    IsNotAr = 'IsNotAr',
    ParkerAlreadyHasSub = 'ParkerAlreadyHasSub',
    Waitlisted = 'Waitlisted',
    InvalidWaitlistSpotsQuantity = 'InvalidWaitlistSpotsQuantity'
}

export function getSubscriptionErrorMessage(errorType: SubscriptionErrorType): string {
    switch (errorType) {
        case SubscriptionErrorType.PlanIsNotListed:
            return "This plan is no longer available for purchase. Please select a different plan.";
        
        case SubscriptionErrorType.FacilityIdMismatch:
            return "There was an error with the facility selection. Please try again.";
        
        case SubscriptionErrorType.SubscriptionNotFound:
            return "We couldn't find your subscription. Please contact support for assistance.";
        
        case SubscriptionErrorType.PlanEntryIsNotLatest:
            return "This plan has been updated. Please refresh the page to see the latest options.";
        
        case SubscriptionErrorType.NoPaymentMethod:
            return "Please add a payment method to continue with your subscription.";
        
        case SubscriptionErrorType.SubscriptionEntryNotFound:
            return "We couldn't find your subscription details. Please contact support.";
        
        case SubscriptionErrorType.PlanEntryNotFound:
            return "The selected plan is no longer available. Please choose a different plan.";
        
        case SubscriptionErrorType.PlanPriceNotFound:
            return "We couldn't retrieve the plan pricing. Please try again or contact support.";
        
        case SubscriptionErrorType.PaymentFailed:
            return "Your payment could not be processed. Please check your payment details and try again.";
        
        case SubscriptionErrorType.OrderNotFound:
            return "We couldn't find your order. Please contact support for assistance.";
        
        case SubscriptionErrorType.CouldNotRecordPayment:
            return "There was an error processing your payment. Please try again or contact support.";
        
        case SubscriptionErrorType.CannotSubscribeToOldPlanEntry:
            return "This plan version is no longer available. Please select a current plan option.";
        
        case SubscriptionErrorType.CouldNotRecordOrder:
            return "There was an error creating your order. Please try again or contact support.";
        
        case SubscriptionErrorType.SubscriptionNotActive:
            return "Your subscription is not active. Please contact support to resolve this issue.";
        
        case SubscriptionErrorType.NoQuantityChange:
            return "No change in quantity was detected. Please adjust the quantity if you wish to make changes.";
        
        case SubscriptionErrorType.QuantityDecrease:
            return "Quantity decreases are not allowed. Please select a higher quantity or contact support.";
        
        case SubscriptionErrorType.InvalidQuantityChange:
            return "The requested quantity change is not valid. Please select a different quantity.";
        
        case SubscriptionErrorType.IsNotAr:
            return "This operation is not available for your account type. Please contact support.";
        
        case SubscriptionErrorType.ParkerAlreadyHasSub:
            return "You already have an active Monthly subscription for this plan.  Please log in to your account to manage your existing subscription.";

        case SubscriptionErrorType.Waitlisted:
            return "Parker is already on the waitlist.";

        case SubscriptionErrorType.InvalidWaitlistSpotsQuantity:
            return "The quantity you are requesting exceeds the available waitlist quantity.";
        
        default:
            return "An unexpected error occurred. Please try again or contact support.";
    }
}

export function isSubscriptionErrorType(error: string): error is SubscriptionErrorType {
    return Object.values(SubscriptionErrorType).includes(error as SubscriptionErrorType);
}

export const showSubscriptionStatus = ( status: DataEntitiesSubscriptionStatus, startDate?: LocalDate | string ): string => {
    const now = LocalDate.now();
    if (startDate) {
        const start = typeof startDate === 'string' ? LocalDate.parse(startDate) : startDate;
        if (start.isAfter(now)) {
            return 'Future';
        }
    }
    switch ( status ) {
        case SubscriptionStatus.Applied:            return 'Pending';
        case SubscriptionStatus.Rejected:           return 'Inactive';
        case SubscriptionStatus.WaitingForContract: return 'Pending';
        case SubscriptionStatus.Active:             return 'Active';
        case SubscriptionStatus.Cancelled:          return 'Cancelled';
        case SubscriptionStatus.Waitlisted:          return 'Waitlisted';
        default:
            return 'Unknown Status';
    }
};

export const getInstructionsShort = ( status: SubscriptionStatus ): string => {
    if( status == SubscriptionStatus.Applied ) {
        return "Under Review";
    }
    if( status == SubscriptionStatus.Rejected ) {
        return "Application Rejected";
    }
    if( status == SubscriptionStatus.WaitingForContract ) {
        return "Waiting for Signature";
    }
    if( status == SubscriptionStatus.Active ) {
        return "Active";
    }
    if( status == SubscriptionStatus.Cancelled ) {
        return "Plan Cancelled";
    }
    if (status == SubscriptionStatus.Waitlisted) {
        return "Waitlisted";
    }
    return "Unknown status";
}

export const getInstructions = ( status: SubscriptionStatus, endDate: LocalDate, hasPaymentMethod: boolean, planEndDate: LocalDate| null ): string => {
    if( status == SubscriptionStatus.Applied ) {
        return "Your application is being reviewed, you should receive e-mail confirmation in 24-48 hours";
    }
    if( status == SubscriptionStatus.Rejected ) {
        return "Your application was rejected.  This is most likely due to lack of capacity of failed payment.  Please contact the facility for more information";
    }
    if( status == SubscriptionStatus.WaitingForContract ) {
        return "Your application was approved, someone should be in touch with a contract shortly.";
    }
    if( status == SubscriptionStatus.Active && planEndDate ) {
        const formatter = DateTimeFormatter.ofPattern('MM/dd/yyyy');
        return `The plan is active and will end on ${planEndDate.format(formatter)}.`;
    }
    if( status == SubscriptionStatus.Active ) {
        return hasPaymentMethod ? "Your plan is active and will auto-renew.  Your card on file will be charged automatically." : "Your plan will be invoiced.";
    }
    if( status == SubscriptionStatus.Cancelled ) {
        const today = LocalDate.now();
      
        let message = "Your plan is cancelled and will not renew.";
        if (endDate.isAfter(today)) {
          message += ` You may park for the remainder of your period until ${showDate(endDate)}.`;
        }
      
        return message;
    }
    if( status == SubscriptionStatus.Waitlisted ) {
        return "Your application is on the waitlist. You’ll be invited to subscribe once spaces become available.";
    }
    return "Unknown status";
}

export const getInstructionsGuest = ( status: SubscriptionStatus ): string => {
    if( status == SubscriptionStatus.Applied ) {
        return "The plan holder's application is under review";
    }
    if( status == SubscriptionStatus.Rejected ) {
        return "The plan holder's application was rejected";
    }
    if( status == SubscriptionStatus.WaitingForContract ) {
        return "A contract has been sent to the plan holder, if they sign the plan will become active ";
    }
    if( status == SubscriptionStatus.Active ) {
        return "The plan is active and will auto-renew. Your card on file will be charged automatically.";
    }
    if( status == SubscriptionStatus.Cancelled ) {
        return "The plan was cancelled and will not renew.";
    }
    if( status == SubscriptionStatus.Waitlisted ) {
        return "The application is on the waitlist. The plan holder will be invited once spaces become available.";
    }
    return "Unknown status";
}

export function isSubscriptionActive( status: SubscriptionStatus ) {
    return status !== SubscriptionStatus.Active;
}

export function isBeforeOrEqual( a: LocalDate, b: LocalDate ) {
    const acond = a.isBefore( b );
    const bcond = b.equals( a );
    return acond || bcond;
}

export function currentSpaces( sub: Subscription ) {
    const now = LocalDate.now();
    const currentQuantity = sub.accessPeriods
                               .filter( ap => isBeforeOrEqual( ap.start, now ) && isBeforeOrEqual( now, ap.end ) )
                               .map( ap => ap.quantity ).reduce( ( a, b ) => a + b, 0 );
    return currentQuantity;
}

export function getOrdinal( n: number ) {
        let suffix = "th";
        if( n % 100 < 11 || n % 100 > 13 ) {
            switch( n % 10 ) {
                case 1: suffix = "st"; break;
                case 2: suffix = "nd"; break;
                case 3: suffix = "rd"; break;
            }
        }
        return n + suffix;
    }
